// web/boxReveal.ts — the PRESENTATION layer for opening an Evolution Box.
//
// Opening a box should be one of the most satisfying actions in BROOD. The
// economy that decides WHAT drops is `web/evolutionBox.ts` (pure, deterministic,
// saved/replayed); this module decides HOW that drop is *revealed* — the staged
// animation, the per-tier/rarity drama, the climactic gene-emergence order, and
// the discovery notifications for the major tiers. It is strictly render-only:
// it reads an already-computed `OpenResult` and touches the DOM, never the sim,
// the save payload, or the state hash.
//
// PURE vs PAINT split (the house discipline, cf. creatureDraw / logistics): the
// rules that make a drop feel like a moment — tier drama profile, emergence
// ordering, which drops earn a notification — are plain functions, unit-tested
// headlessly (web/boxReveal.test.mjs). Only the particle/beam spawners touch
// `document`, and they're called lazily so importing the module needs no DOM.

import { BOX_TIERS, shortRewardName } from './evolutionBox.ts';
import type { OpenResult, RewardGene } from './evolutionBox.ts';
import { RARITY } from '../src/types.ts';
import { RARITY_NAME } from '../src/sim/geneData.ts';

/** Tier index that begins the "major drop" band (Mutation+). Adaptation (0) is everyday. */
export const FIRST_MAJOR_TIER = 1;

/** The dramatic profile for a tier — drives the whole reveal sequence. */
export interface TierEffect {
  tier: number;
  /** Box name (BOX_TIERS[tier]). */
  name: string;
  /** A "major drop" the brief calls out (Mutation / Aberration / Apex / Miracle). */
  major: boolean;
  /** Centerpiece banner text ('' for Adaptation), escalating with tier. */
  banner: string;
  /** Anticipation shake duration (ms) before the box bursts — longer = bigger. */
  shakeMs: number;
  /** Per-gene reveal stagger (ms) — slower for big tiers so each lands. */
  stepMs: number;
  /** Burst particle count (scales the explosion). */
  particles: number;
  /** Radial light beams behind the box (Aberration+). */
  beams: boolean;
  /** Full-screen flash class ('' | 'epic' | 'miracle'). */
  flash: string;
  /** Screen-shake the whole overlay (Apex+). */
  quake: boolean;
}

const TIER_BANNERS: readonly string[] = [
  '', 'MUTATION', 'ABERRATION', '✦ APEX ✦', '✦✦ EVOLUTIONARY MIRACLE ✦✦',
];
const TIER_SHAKE_MS: readonly number[] = [320, 520, 680, 880, 1150];
const TIER_STEP_MS: readonly number[] = [260, 300, 340, 420, 520];
const TIER_PARTICLES: readonly number[] = [10, 16, 26, 38, 56];

/** Resolve a tier index into its full reveal drama profile. */
export function tierEffect(tier: number): TierEffect {
  const t = tier < 0 ? 0 : tier > 4 ? 4 : tier;
  return {
    tier: t,
    name: BOX_TIERS[t] as string,
    major: t >= FIRST_MAJOR_TIER,
    banner: TIER_BANNERS[t] as string,
    shakeMs: TIER_SHAKE_MS[t] as number,
    stepMs: TIER_STEP_MS[t] as number,
    particles: TIER_PARTICLES[t] as number,
    beams: t >= 2,
    flash: t >= 4 ? 'miracle' : t >= 3 ? 'epic' : '',
    quake: t >= 3,
  };
}

/**
 * Climactic ordering: emerge the rewards LEAST → MOST rare so the best gene is
 * the finale, not buried mid-sequence. Stable within a rarity (preserves the
 * forge order) and non-mutating (returns a fresh array). A real (kept) gene
 * outranks a duplicate of equal rarity so the keeper lands last.
 */
export function emergenceOrder(genes: readonly RewardGene[]): RewardGene[] {
  return genes
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      if (a.g.rarity !== b.g.rarity) return a.g.rarity - b.g.rarity;
      const ad = a.g.duplicate ? 0 : 1;
      const bd = b.g.duplicate ? 0 : 1;
      if (ad !== bd) return ad - bd; // duplicates first, keeper last
      return a.i - b.i;              // else stable
    })
    .map((x) => x.g);
}

/** Per-card flourish intensity (0 plain … 3 legendary) → CSS effect strength. */
export function rarityFlourish(rarity: number): number {
  if (rarity >= RARITY.Legendary) return 3;
  if (rarity >= RARITY.Epic) return 2;
  if (rarity >= RARITY.Rare) return 1;
  return 0;
}

/** The single best NON-duplicate gene in a result, or null (all dups / empty). */
export function headlineGene(result: OpenResult): RewardGene | null {
  let best: RewardGene | null = null;
  for (const g of result.genes) {
    if (g.duplicate) continue;
    if (best === null || g.rarity > best.rarity) best = g;
  }
  return best;
}

/** A discovery banner to surface in the notification feed (outlives the overlay). */
export interface Discovery {
  icon: string;
  title: string;
  /** Styling class: 'major' | 'apex' | 'miracle' (drives colour/prominence). */
  cls: string;
}

/**
 * The memorable-moment notifications for an open. At most two — a tier banner for
 * a major drop, and the headline gene if it's Epic+ — so the feed celebrates
 * without spamming. `firstSeen` (this tier never opened before this session) earns
 * a "NEW" flourish.
 */
export function discoveries(result: OpenResult, firstSeen: boolean): Discovery[] {
  const out: Discovery[] = [];
  const eff = tierEffect(result.tier);
  if (eff.major) {
    const cls = result.tier >= 4 ? 'miracle' : result.tier >= 3 ? 'apex' : 'major';
    const icon = result.tier >= 4 ? '✦' : result.tier >= 3 ? '★' : '✧';
    const lead = firstSeen ? 'NEW · ' : '';
    out.push({ icon, title: `${lead}${eff.name} Box opened`, cls });
  }
  const head = headlineGene(result);
  if (head !== null && head.rarity >= RARITY.Epic) {
    const cls = head.rarity >= RARITY.Legendary ? 'miracle' : 'apex';
    out.push({
      icon: '🧬',
      title: `${RARITY_NAME[head.rarity] as string} discovered: ${shortRewardName(head.type, head.rarity)}`,
      cls,
    });
  }
  return out;
}

// --- DOM paint helpers (lazy; only these touch `document`) -------------------

/** Even, organic scatter of a burst particle's angle/distance from an index. */
function scatter(i: number, n: number): { angle: number; dist: number } {
  const GOLDEN = 2.39996323; // golden angle → pleasing non-repeating spread
  const angle = (i * GOLDEN) % (Math.PI * 2);
  const frac = (i * 0.6180339887) % 1; // deterministic 0..1 jitter
  const dist = 70 + frac * 90;          // px from center
  return { angle, dist };
}

// --- Burst-FX element POOL (render-only; reused across opens) ----------------
//
// `playBurstFx` used to spawn up to MAX_PARTICLES + MAX_BEAMS fresh DOM nodes per
// open and abandon them. That's fine for one modal open at a time, but it allocates
// N nodes every reveal — wasteful if reveals ever stack or animate on the live
// canvas. Instead we keep a single module-level pool of reusable <span>s: each open
// RE-ACQUIRES and RE-STYLES them rather than creating new ones, and they're returned
// (detached from the DOM — a removed node renders nothing) when the burst finishes.
// The pool is capped at the largest tier's counts, so memory is bounded no matter
// how many opens occur.
//
// Visual parity: detaching every node (innerHTML='') and re-appending the needed
// ones reconnects them, which RESTARTS their CSS animations from 0% — so a reused
// particle/beam animates identically to a freshly-created one. All per-node CSS vars
// (--dx/--dy/--delay/--scale, --rot) and the tier tint (the layer className) are
// re-set each open, so nothing stale carries over between tiers.

/** Pool cap = the biggest tier's burst counts (tier 4: 56 particles, 12 beams). */
const MAX_PARTICLES = TIER_PARTICLES[TIER_PARTICLES.length - 1] as number;
const MAX_BEAMS = 12;

/** Longest burst-FX lifetime (ms): particle anim .85s + max stagger 140ms, beam .9s. */
const FX_LIFETIME_MS = 1000;

/** The reusable element pool — created lazily on first burst, then reused forever. */
const fxPool: { particles: HTMLSpanElement[]; beams: HTMLSpanElement[] } = {
  particles: [],
  beams: [],
};

/** Pending return-to-pool timer id, so overlapping opens reschedule cleanly. */
let fxReturnTimer: ReturnType<typeof setTimeout> | null = null;

/** Borrow pooled element `i` of `kind`, creating it (capped) on first use. */
function acquire(kind: 'particle' | 'beam', i: number): HTMLSpanElement {
  const store = kind === 'particle' ? fxPool.particles : fxPool.beams;
  let el = store[i];
  if (el === undefined) {
    el = document.createElement('span');
    el.className = kind === 'particle' ? 'bx-particle' : 'bx-beam';
    store[i] = el;
  }
  return el;
}

/**
 * Detach every pooled node from its layer — the "return to pool" step. Detaching
 * (rather than destroying) keeps the nodes available for the next open and stops
 * their finished animations from lingering in the DOM. Idempotent and
 * self-contained: also clears the pending timer. Called when the burst animation
 * ends (and defensively at the start of each open).
 */
function returnFxToPool(): void {
  if (fxReturnTimer !== null) {
    clearTimeout(fxReturnTimer);
    fxReturnTimer = null;
  }
  for (let i = 0; i < fxPool.particles.length; i++) (fxPool.particles[i] as HTMLSpanElement).remove();
  for (let i = 0; i < fxPool.beams.length; i++) (fxPool.beams[i] as HTMLSpanElement).remove();
}

/**
 * Spawn the burst FX (particles + optional beams) into a layer element, sized to
 * the tier. Reuses a capped pool of <span> nodes instead of allocating fresh ones
 * each open, returning them (detached) when the animation ends. Clears any prior FX
 * first. Pure-cosmetic + deterministic (no RNG); CSS drives the motion. The caller
 * may also clear the layer when the overlay closes — that simply detaches the
 * pooled nodes early, which is harmless (the next open re-acquires + re-appends).
 */
export function playBurstFx(layer: HTMLElement, tier: number): void {
  const eff = tierEffect(tier);
  // Return any still-active nodes first (a rapid re-open before the prior burst
  // finished), then clear whatever else is in the layer, so we start clean.
  returnFxToPool();
  layer.innerHTML = '';
  layer.className = `box-fx tier-${eff.tier}`;

  const particles = eff.particles > MAX_PARTICLES ? MAX_PARTICLES : eff.particles;
  for (let i = 0; i < particles; i++) {
    const { angle, dist } = scatter(i, particles);
    const p = acquire('particle', i);
    p.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
    p.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
    p.style.setProperty('--delay', `${(i % 6) * 28}ms`);
    p.style.setProperty('--scale', `${0.6 + ((i * 0.37) % 1) * 0.9}`);
    layer.appendChild(p); // (re)connecting restarts the particle's CSS animation
  }
  if (eff.beams) {
    const beamCount = eff.tier >= 4 ? MAX_BEAMS : 8;
    for (let i = 0; i < beamCount; i++) {
      const b = acquire('beam', i);
      b.style.setProperty('--rot', `${(360 / beamCount) * i}deg`);
      layer.appendChild(b); // (re)connecting restarts the beam's CSS animation
    }
  }

  // Return the borrowed nodes to the pool once the burst has played out. Guarded so
  // a headless/SSR import (no `setTimeout`) simply skips the auto-return; the next
  // open's `returnFxToPool()` still cleans up.
  if (typeof setTimeout === 'function') {
    fxReturnTimer = setTimeout(returnFxToPool, FX_LIFETIME_MS);
  }
}
